import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * ── A PÁGINA NOVA COMEÇA NO TOPO ─────────────────────────────────────────────
 *
 * ## O que o dono viu
 *
 * *«Quando um curso tem muitos módulos e conteúdos, a tela fica grande demais;
 * ao clicar num link, a tela fica no mesmo lugar em vez de rolar para o início
 * da página.»* É a forma de uma SPA: o navegador só rola ao topo em navegação
 * de documento, e o React Router troca a tela sem trocar o documento — quem
 * clicou na aula 11, lá embaixo da lista, chega na aula 11 olhando para o
 * rodapé dela.
 *
 * ## A regra
 *
 * - **Caminho novo** (clique num link, `navigate`): rola ao topo, sem animação
 *   (uma rolagem animada de 3 000 px é um segundo de tela passando).
 * - **Voltar/avançar** (`POP`): NÃO mexe — o navegador restaura a posição de
 *   onde a pessoa saiu, e é isso que quem aperta «voltar» espera.
 * - **Âncora** (`#certificado`): NÃO mexe — a página do curso já a leva a
 *   sério (ver `PaginaDoCurso`), e rolar ao topo aqui anularia o link.
 * - **Só a busca mudou** (`?aula=` no mural): NÃO mexe — é filtro, não página.
 *
 * Fora das rotas, ao lado da faixa e do portão, pelo mesmo motivo deles: vale
 * para toda tela, e não desmonta a cada navegação.
 */
export function RolarAoTopo() {
  const { pathname, hash, state } = useLocation();
  const tipo = useNavigationType();
  const anterior = useRef(pathname);
  /*
   * ── E UM POPUP NÃO É UMA PÁGINA NOVA (18/09/2026) ─────────────────────────
   *
   * Abrir o post do mural em popup É uma navegação — o endereço muda, e tem de
   * mudar, senão o link não existe. Mas a página que se vê continua a ser o
   * mural, por trás do popup: rolar ao topo aqui jogaria fora exatamente o
   * lugar que o popup existe para guardar. Quem o diz é `state.fundo`, posto
   * por quem abriu (ver `App.tsx`).
   */
  const ehPopup = !!(state as { fundo?: unknown } | null)?.fundo;

  useEffect(() => {
    if (pathname === anterior.current) return;
    anterior.current = pathname;
    if (tipo === 'POP' || hash || ehPopup) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash, tipo, ehPopup]);

  return null;
}
